import { Link, useFetcher } from "@remix-run/react";
import { clsx } from "clsx";
import * as React from "react";

import { Container } from "~/components/container";
import { FadeIn } from "~/components/fade-in";
import { socialMediaProfiles } from "~/components/social-media";
import { delivery } from "~/lib/site";
import { action as subscribeToNewsletterAction } from "~/routes/[_]actions.newsletter";

import { Svg } from "./svg";

const navigation = [
  {
    title: "Company",
    links: [
      { title: "How we deliver", href: "/#delivery" },
      { title: "About", href: "/about" },
      { title: "Start a project", href: "/contact" },
    ],
  },
  { title: "Connect", links: socialMediaProfiles },
];

function Navigation() {
  return (
    <nav>
      <ul className="grid grid-cols-2 gap-8">
        {navigation.map((section) => (
          <li key={section.title}>
            <div className="label text-ink-400">{section.title}</div>
            <ul className="mt-5 space-y-3 text-sm text-ink-300">
              {section.links.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="transition hover:text-ink-50">
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function ArrowIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg viewBox="0 0 16 6" aria-hidden="true" {...props}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16 3 10 .5v2H0v1h10v2L16 3Z"
      />
    </svg>
  );
}

function NewsletterForm() {
  let fetcher = useFetcher<typeof subscribeToNewsletterAction>();
  let inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.ok && inputRef.current) {
      inputRef.current.value = "";
    }
  }, [fetcher.state, fetcher.data]);

  return (
    <div className="max-w-sm">
      <fetcher.Form
        method="post"
        action="/_actions/newsletter"
        className={clsx(fetcher.state === "submitting" && "opacity-50")}
      >
        <div className="label text-ink-400">Notes on shipping mobile</div>
        <p className="mt-5 text-sm text-ink-300">
          What we learn shipping Kotlin Multiplatform, Compose and SwiftUI to
          the stores. Only when we have something worth sending.
        </p>
        <div className="relative mt-6">
          <input
            type="email"
            placeholder="Email address"
            name="email"
            autoComplete="email"
            aria-label="Email address"
            className="block w-full rounded-full border border-ink-600 bg-transparent py-3.5 pl-5 pr-16 text-base/6 text-ink-50 transition placeholder:text-ink-400 focus:border-accent-400 focus:outline-none"
            ref={inputRef}
            aria-invalid={fetcher.data?.errors?.email ? "true" : undefined}
          />
          <div className="absolute inset-y-1 right-1 flex justify-end">
            <button
              type="submit"
              aria-label="Submit"
              className="flex aspect-square h-full items-center justify-center rounded-full bg-ink-50 text-ink-950 transition hover:bg-white"
              onClick={(event) => {
                if (fetcher.state === "submitting") event.preventDefault();
              }}
            >
              <ArrowIcon className="w-4" />
            </button>
          </div>
        </div>
      </fetcher.Form>

      <div aria-live="polite" className="pt-4 text-sm">
        {fetcher.state === "idle" && fetcher.data?.ok ? (
          <p className="text-ink-300">
            <b className="font-semibold text-ink-50">Got it.</b> Check your
            email to confirm — otherwise nothing will reach you.
          </p>
        ) : null}
        {fetcher.state === "idle" && fetcher.data?.errors?.email ? (
          <p className="text-red-400">{fetcher.data.errors.email}</p>
        ) : null}
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <Container as="footer" className="mt-24 w-full sm:mt-32">
      <FadeIn className="border-t border-ink-800 pt-14">
        <div className="grid grid-cols-1 gap-x-8 gap-y-14 lg:grid-cols-2">
          <Navigation />
          <div className="flex lg:justify-end">
            <NewsletterForm />
          </div>
        </div>
        <div className="mb-16 mt-16 flex flex-wrap items-end justify-between gap-x-6 gap-y-4 border-t border-ink-800 pt-10">
          <Link to="/" aria-label="Home" className="text-ink-50">
            <Svg name="logo-full" className="h-8 w-36" />
          </Link>
          <div className="text-sm text-ink-400">
            <Link
              to={`mailto:${delivery.email}`}
              className="transition hover:text-ink-50"
            >
              {delivery.email}
            </Link>
            <span className="px-2 text-ink-600">·</span>© bmcreations{" "}
            {new Date().getFullYear()}
          </div>
        </div>
      </FadeIn>
    </Container>
  );
}
